import { useSearchParams, Link } from 'react-router-dom';
import { FileText, TrendingUp, ClipboardCheck, Info, ChevronRight, DollarSign } from 'lucide-react';
import ReportsListTab from '../../components/reporting/ReportsListTab';
import AgentPerformanceTab from '../../components/reporting/AgentPerformanceTab';

type TabType = 'reports' | 'agent-performance';

// Mock data for recent day scores - in production this would come from the API
const recentDayScores = [
  {
    date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    dayName: 'Yesterday',
    overallScore: 82.3,
    serviceLevel: { actual: 81.2, target: 85, weight: 40 },
    occupancy: { actual: 78.5, target: 80, weight: 30 },
    aht: { actual: 402, target: 360, weight: 30 }, // AHT in seconds (target: 6 min, actual: 6:42)
  },
  {
    date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    dayName: '2 days ago',
    overallScore: 88.5,
    serviceLevel: { actual: 86.5, target: 85, weight: 40 },
    occupancy: { actual: 76.0, target: 80, weight: 30 },
    aht: { actual: 372, target: 360, weight: 30 }, // AHT in seconds (target: 6 min, actual: 6:12)
  },
  {
    date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    dayName: '3 days ago',
    overallScore: 96.8,
    serviceLevel: { actual: 88.0, target: 85, weight: 40 },
    occupancy: { actual: 79.2, target: 80, weight: 30 },
    aht: { actual: 354, target: 360, weight: 30 }, // AHT in seconds (target: 6 min, actual: 5:54) - at goal
  },
];

// Format seconds as m:ss
const formatAHT = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Calculate AHT score (100% if at or below target, decreases if above)
const getAHTScore = (actual: number, target: number): number => {
  if (actual <= target) return 100; // At or below goal = 100%
  // Above goal: score decreases proportionally
  return (target / actual) * 100;
};

// Get AHT variance display (e.g., "+0:42" or "On target")
const getAHTVariance = (actual: number, target: number): { text: string; isOver: boolean } => {
  const diff = actual - target;
  if (diff <= 0) {
    return { text: 'On target', isOver: false };
  }
  const pctOver = ((diff / target) * 100).toFixed(0);
  return { text: `+${formatAHT(diff)} (+${pctOver}%)`, isOver: true };
};

const getScoreColor = (score: number) => {
  if (score >= 85) return 'text-green-600 bg-green-100';
  if (score >= 70) return 'text-yellow-600 bg-yellow-100';
  return 'text-red-600 bg-red-100';
};

const getScoreBgColor = (score: number) => {
  if (score >= 85) return 'bg-green-500';
  if (score >= 70) return 'bg-yellow-500';
  return 'bg-red-500';
};

export default function ReportingPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive active tab directly from URL (single source of truth)
  const urlTab = searchParams.get('tab');
  const activeTab: TabType = urlTab === 'agent-performance' ? 'agent-performance' : 'reports';

  // Handler to change tabs - directly updates URL
  const handleTabChange = (tab: TabType) => {
    setSearchParams({ tab }, { replace: true });
  };

  // Calculate 7-day average
  const avgScore = recentDayScores.reduce((sum, d) => sum + d.overallScore, 0) / recentDayScores.length;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Reporting & Analytics</h1>
        <p className="text-gray-600">
          Generate reports, analyze performance, and export data
        </p>
      </div>

      {/* Day in Review Section */}
      <div className="mb-6 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-purple-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="bg-purple-100 p-3 rounded-lg">
                <ClipboardCheck className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Day in Review</h3>
                <p className="text-sm text-gray-600">
                  Daily operational performance scores
                </p>
              </div>
            </div>
            <Link
              to="/reporting/day-in-review"
              className="flex items-center space-x-1 text-purple-600 text-sm font-medium hover:text-purple-700"
            >
              <span>View Details</span>
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Score Calculation Explanation */}
        <div className="px-4 py-3 bg-white/50 border-b border-purple-100">
          <div className="flex items-center space-x-2 text-sm text-purple-700">
            <Info className="w-4 h-4" />
            <span>
              <strong>Score Formula:</strong> (Service Level × 40%) + (Occupancy × 30%) + (AHT × 30%)
            </span>
          </div>
        </div>

        {/* Recent Scores Grid */}
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-700">Recent Day Scores</h4>
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-500">3-Day Avg:</span>
              <span className={`text-sm font-bold px-2 py-0.5 rounded ${getScoreColor(avgScore)}`}>
                {avgScore.toFixed(1)}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {recentDayScores.map((day) => (
              <Link
                key={day.date}
                to={`/reporting/day-in-review?date=${day.date}`}
                className="bg-white rounded-lg p-4 border border-purple-100 hover:border-purple-300 hover:shadow-sm transition-all"
              >
                {/* Day Header */}
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{day.dayName}</div>
                    <div className="text-xs text-gray-500">
                      {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                  </div>
                  <div className={`text-2xl font-bold px-2 py-1 rounded ${getScoreColor(day.overallScore)}`}>
                    {day.overallScore.toFixed(1)}
                  </div>
                </div>

                {/* Score Breakdown */}
                <div className="space-y-2">
                  {/* Service Level */}
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Service Level (40%)</span>
                    <span className="font-medium text-gray-700">{day.serviceLevel.actual.toFixed(1)}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${getScoreBgColor((day.serviceLevel.actual / day.serviceLevel.target) * 100)}`}
                      style={{ width: `${Math.min((day.serviceLevel.actual / day.serviceLevel.target) * 100, 100)}%` }}
                    />
                  </div>

                  {/* Occupancy */}
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Occupancy (30%)</span>
                    <span className="font-medium text-gray-700">{day.occupancy.actual.toFixed(1)}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${getScoreBgColor((day.occupancy.actual / day.occupancy.target) * 100)}`}
                      style={{ width: `${Math.min((day.occupancy.actual / day.occupancy.target) * 100, 100)}%` }}
                    />
                  </div>

                  {/* Average Handle Time */}
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">AHT (30%)</span>
                    <span className={`font-medium ${getAHTVariance(day.aht.actual, day.aht.target).isOver ? 'text-red-600' : 'text-green-600'}`}>
                      {getAHTVariance(day.aht.actual, day.aht.target).text}
                    </span>
                  </div>
                  <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${getScoreBgColor(getAHTScore(day.aht.actual, day.aht.target))}`}
                      style={{ width: `${Math.min(getAHTScore(day.aht.actual, day.aht.target), 100)}%` }}
                    />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Savings Report Section */}
      <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg overflow-hidden">
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="bg-green-100 p-3 rounded-lg">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Savings Report</h3>
                <p className="text-sm text-gray-600">
                  Track cost savings from WFM optimization - headcount, overtime, and labor costs
                </p>
              </div>
            </div>
            <Link
              to="/reports/savings"
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
            >
              <span>View Savings Report</span>
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Tab Headers */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => handleTabChange('reports')}
              className={`
                flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm
                ${
                  activeTab === 'reports'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              <FileText className="h-5 w-5" />
              <span>Reports</span>
            </button>
            <button
              onClick={() => handleTabChange('agent-performance')}
              className={`
                flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm
                ${
                  activeTab === 'agent-performance'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              <TrendingUp className="h-5 w-5" />
              <span>Agent Performance</span>
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'reports' && <ReportsListTab />}
          {activeTab === 'agent-performance' && <AgentPerformanceTab />}
        </div>
      </div>
    </div>
  );
}
